import { createContext, useContext, useMemo, useState, useCallback } from 'react';

import { type FilterStateType, type WorklistFiltersType } from '../../WorklistFilters/types';
import { useGenerateFilterConfigV2 } from '../../WorklistFilters/hooks/useGenerateFilterConfigV2';
import { useWorklistURLParams } from '../useWorklistURLParams';
import { parseFiltersFromURLParams, applyToggleDefaults } from '../../WorklistFilters/utils';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useFetchDefaultCustomWorklistFilters } from '../hooks/useFetchDefaultCustomWorklistFilters';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { logger } from 'modules/logger';
import { useRecoilState } from 'recoil';
import { activeCustomWorklistIDsState } from '../state';
import { useCustomWorklists } from '../hooks/useCustomWorklists';
import {
  useCustomWorklistFilterPreferences,
  useCustomWorklistPreferences,
} from '../hooks/useCustomWorklistPreferences';
import { useCustomWorklistPreferenceUpdates } from '../hooks/useCustomWorklistPreferenceUpdates';
import { telemetryContextFor } from 'modules/Apollo/types';
import {
  describeFilters,
  getChangedFilterKeys,
} from 'domains/worklist/WorklistFilters/describeFilters';

type WorklistFiltersContextV2Bag = {
  filters: FilterStateType;
  updateFilters: (filters: FilterStateType) => void;
  replaceFilters: (filters: FilterStateType) => void;
  resetAllFilters: () => void;
  resetFilter: (key: WorklistFiltersType) => void;
};

type WorklistFiltersContextV2ProviderProps = {
  children: React.ReactNode;
  overrideURLSearchParams?: Nullable<URLSearchParams>;
  defaultCustomWorklistIDs?: string[];
  skipWorklistQueries?: boolean;
};

const WorklistFiltersContextV2 = createContext<WorklistFiltersContextV2Bag | null>(null);

export function WorklistFiltersContextV2Provider({
  children,
  overrideURLSearchParams,
  defaultCustomWorklistIDs = [],
  skipWorklistQueries = false,
}: WorklistFiltersContextV2ProviderProps): React.ReactElement {
  const [isRefactorEnabled] = useFeatureFlagEnabled(FF.WORKLIST_CONTEXT_REFACTOR);

  const [activeCustomWorklistIDs] = useRecoilState(activeCustomWorklistIDsState);
  const { isPublicOrCombinedWorklist, privateWorklists } = useCustomWorklists({
    queryOptions: {
      context: APOLLO_QUERY_CONTEXT,
      skip: skipWorklistQueries,
    },
  });
  const { data: currentUserData } = useCurrentUser({
    context: APOLLO_QUERY_CONTEXT,
    skip: skipWorklistQueries,
  });

  const isPublicOrCombined = isPublicOrCombinedWorklist(activeCustomWorklistIDs);
  const { availableFilters } = useGenerateFilterConfigV2({
    context: APOLLO_QUERY_CONTEXT,
    skipQueries: skipWorklistQueries,
  });
  const { filters: urlFilters } = useWorklistURLParams(overrideURLSearchParams);

  const customWorklistFilterPreferences = useCustomWorklistFilterPreferences({
    worklistIds: activeCustomWorklistIDs,
    isPublicOrCombined,
    context: APOLLO_QUERY_CONTEXT,
    skipQueries: skipWorklistQueries,
  });

  const customWorklistPreferences = useCustomWorklistPreferences({
    worklistIds: activeCustomWorklistIDs,
    isPublicOrCombined,
    context: APOLLO_QUERY_CONTEXT,
    skipQueries: skipWorklistQueries,
  });

  const { updateCustomWorklistFilterSettings } = useCustomWorklistPreferenceUpdates({
    activeCustomWorklistIDs,
    privateWorklists,
    worklistSettings: currentUserData?.me?.worklistSettings,
    customWorklistSettings: customWorklistPreferences,
    userSmid: currentUserData?.me?.id,
    skipQueries: skipWorklistQueries,
    context: APOLLO_QUERY_CONTEXT,
  });

  const shouldUseCustomWorklistPreferences = useMemo(() => {
    return activeCustomWorklistIDs.length > 0;
  }, [activeCustomWorklistIDs.length]);

  const resolvedInitialFilters = useMemo(() => {
    // Only initialize filters when refactor flag is enabled
    if (!isRefactorEnabled) {
      return {};
    }

    let baseFilters: FilterStateType = {};

    if (shouldUseCustomWorklistPreferences && customWorklistFilterPreferences) {
      baseFilters = customWorklistFilterPreferences;
    } else if (urlFilters && availableFilters) {
      // URL params are transient navigation state (deep-links, shared URLs) and always apply.
      // The persistence gate lives on the write path and the saved-prefs read path.
      const urlParsedFilters = parseFiltersFromURLParams(urlFilters, availableFilters);
      baseFilters = urlParsedFilters;
    }

    return applyToggleDefaults(baseFilters, availableFilters);
  }, [
    isRefactorEnabled,
    availableFilters,
    urlFilters,
    shouldUseCustomWorklistPreferences,
    customWorklistFilterPreferences,
  ]);

  const [filters, setFilters] = useState<FilterStateType>(resolvedInitialFilters);

  const saveFilterPreferences = useCallback(
    (updatedFilters: FilterStateType) => {
      if (!shouldUseCustomWorklistPreferences) {
        return;
      }

      updateCustomWorklistFilterSettings(updatedFilters);
    },
    [shouldUseCustomWorklistPreferences, updateCustomWorklistFilterSettings]
  );

  const updateFilters = useCallback(
    (incoming: FilterStateType) => {
      setFilters((prev) => {
        const next = { ...prev };
        Object.entries(incoming).forEach(([key, value]) => {
          next[key as WorklistFiltersType] = value as any;
        });

        logger.info(
          `[WorklistFiltersContextV2] Filters updated${activeCustomWorklistIDs.length > 0 ? ' for custom worklist: ' + activeCustomWorklistIDs.join(', ') : ''}`,
          {
            changedKeys: getChangedFilterKeys(prev, next),
            previousFilters: describeFilters(prev),
            incomingFilters: describeFilters(incoming),
            newFilters: describeFilters(next),
            customWorklistIDs: activeCustomWorklistIDs,
          }
        );

        saveFilterPreferences(next);
        return next;
      });
    },
    [activeCustomWorklistIDs, saveFilterPreferences]
  );

  const replaceFilters = useCallback(
    (nextFilters: FilterStateType) => {
      const newFilters = applyToggleDefaults(nextFilters, availableFilters);
      setFilters(newFilters);

      logger.info(
        `[WorklistFiltersContextV2] Filters replaced${activeCustomWorklistIDs.length > 0 ? ' for custom worklist: ' + activeCustomWorklistIDs.join(', ') : ''}`,
        {
          newFilters: describeFilters(newFilters),
          customWorklistIDs: activeCustomWorklistIDs,
        }
      );

      saveFilterPreferences(newFilters);
    },
    [activeCustomWorklistIDs, availableFilters, saveFilterPreferences]
  );

  const resetAllFilters = useCallback(() => {
    const newFilters = applyToggleDefaults({}, availableFilters);
    setFilters(newFilters);

    logger.info(
      `[WorklistFiltersContextV2] All filters reset${activeCustomWorklistIDs.length > 0 ? ' for custom worklist: ' + activeCustomWorklistIDs.join(', ') : ''}`,
      {
        newFilters: describeFilters(newFilters),
        customWorklistIDs: activeCustomWorklistIDs,
      }
    );

    saveFilterPreferences(newFilters);
  }, [activeCustomWorklistIDs, availableFilters, saveFilterPreferences]);

  const resetFilter = useCallback(
    (key: WorklistFiltersType) => {
      setFilters((prev) => {
        if (!(key in prev)) {
          return prev;
        }
        const { [key]: _removed, ...remainingFilters } = prev;

        logger.info(
          `[WorklistFiltersContextV2] Filter ${key} reset${activeCustomWorklistIDs.length > 0 ? ' for custom worklist: ' + activeCustomWorklistIDs.join(', ') : ''}`,
          {
            filterKey: key,
            previousValue: describeFilters({ [key]: prev[key] })?.[key],
            newFilters: describeFilters(remainingFilters),
            customWorklistIDs: activeCustomWorklistIDs,
          }
        );

        saveFilterPreferences(remainingFilters);
        return remainingFilters;
      });
    },
    [activeCustomWorklistIDs, saveFilterPreferences]
  );

  // Fetches default custom worklist filters on mount (only when refactor flag is enabled)
  useFetchDefaultCustomWorklistFilters({
    defaultCustomWorklistIDs: isRefactorEnabled ? defaultCustomWorklistIDs : [],
    replaceFilters,
    context: APOLLO_QUERY_CONTEXT,
    skipQueries: skipWorklistQueries,
  });

  const value = useMemo(
    () => ({
      filters,
      updateFilters,
      replaceFilters,
      resetAllFilters,
      resetFilter,
    }),
    [filters, replaceFilters, resetAllFilters, resetFilter, updateFilters]
  );

  return (
    <WorklistFiltersContextV2.Provider value={value}>{children}</WorklistFiltersContextV2.Provider>
  );
}

export function useWorklistFiltersContextV2(): WorklistFiltersContextV2Bag {
  const context = useContext(WorklistFiltersContextV2);
  if (context == null) {
    throw new Error(
      'useWorklistFiltersContextV2 must be used within a WorklistFiltersContextV2Provider.'
    );
  }
  return context;
}

export type { WorklistFiltersContextV2Bag };

WorklistFiltersContextV2Provider.displayName = 'WorklistFiltersContextV2Provider';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(WorklistFiltersContextV2Provider);
